
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import CategoryPageLayout from '../../components/feature/CategoryPageLayout';
import FilterTabs from '../../components/feature/FilterTabs';
import CasinoCard from '../../components/feature/CasinoCard';
import CasinoReviewPanel from '../../components/feature/CasinoReviewPanel';
import { casinos } from '../../mocks/casinos';

const normalizeCasinoName = (name: string) =>
  name.toLowerCase().replace(/\sonline casino/g, '').replace(/[^a-z0-9]/g, '');

const filterNameMap: Record<string, string[]> = {
  New: ['ipay9vip', 'mrboom9'],
  'Best Bonus': ['bigpay77', 'me99', 'gucci9', 'spinaroo', 'mrbean9', 'queen13'],
  'Fast Payout': ['ipay9', 'kingbet9', 'rolex9', 'micky9', 'winnie777', 'bybid9'],
  Mobile: ['me99', 'rolex9', 'gucci9', 'mrbean9', 'spinaroo', 'bybid9', 'micky9', 'winnie777', 'queen13'],
};

const reviewCriteria = [
  {
    title: 'Bonuses',
    iconImage: '/methodology-icons/money-bag-dollar.svg',
    description:
      "Our experts check real money online casino bonuses are easy to claim and quickly added to your bankroll. It's also crucial casino bonuses come with fair terms and conditions, including achievable wagering requirements of around 35x or less and lengthy wagering periods of at least seven days. We approve the bonuses at all of our recommended online casinos with real money gaming on this page.",
  },
  {
    title: 'Casino games',
    iconImage: '/methodology-icons/games-software.svg',
    description:
      "We test real money online casino games and monitor the return-to-player rate (RTP) to determine how often they pay out. Whether you love slots, playing classic table games, or checking out niche categories, there should be at least 500 games available. Our experts also ensure the games at our shortlisted sites come from respected software providers and offer smooth gameplay and instant payouts. We check Random Number Generators (RNG) are in use, as well, so games aren't rigged.",
  },
  {
    title: 'Payment methods',
    iconImage: '/methodology-icons/payment.svg',
    description:
      "Trustworthy casinos list world-renowned debit and credit cards, bank transfers, and e-wallets among their payment methods. We always make three separate real money casino deposits to test each site's processing time and look out for instant availability of funds. Likewise, our experts withdraw their winnings from testing games and score sites with the fastest payouts higher, especially if they come through in less than 24 hours.",
  },
  {
    title: 'Safety and licensing',
    iconImage: '/methodology-icons/lock.svg',
    description:
      'We rate the transparency and quality of real money online casino privacy policies, as well as checking for SSL encryption protecting your personal and financial information. Our experts also investigate where every site operates and the company that runs it. We check for up-to-date certification from the likes of the Curacao Gaming Authority (CGA), PAGCOR, Malta Gaming Authority (MGA), and state or provincial entities in the Australia. Top marks go to sites audited by external bodies, such as eCOGRA.',
  },
  {
    title: 'Mobile casino experience',
    iconImage: '/methodology-icons/mobile.svg',
    description:
      'HTML5 technology ensures a seamless transition from desktop to handheld devices but we deep dive, testing every mobile casino experience. We review each aspect of real money casinos on phone and tablet browsers, from the speed of logging in to the ease of withdrawing winnings. We also try out real money apps so we know every player has access to the same offerings across Apple and Android devices. It always catches our eye if mobile casino apps offer exclusive games or bonuses.',
  },
  {
    title: 'Customer support',
    iconImage: '/methodology-icons/get-in-touch.svg',
    description:
      "We check online casinos offer extensive Frequently Asked Questions (FAQs) sections, 24/7 live chat, email, and phone support in multiple languages. Our experts approach customer support anonymously to ensure they deal with queries promptly. We also look to make sure companies help anyone who's struggling with real money online gaming. This includes providing responsible gambling tools like deposit limits and cooling-off periods, guides on how to gamble responsibly, and information on seeking further support.",
  },
];

const popularPaymentMethods = [
  { name: 'Apple Pay', icon: '/payment-icons/apple-pay.png' },
  { name: 'Amopay', icon: '/payment-icons/amopay.png' },
  { name: 'Mastercard', icon: '/payment-icons/mastercard.png' },
  { name: 'Google Pay', icon: '/payment-icons/google-pay.png' },
  { name: 'SpeedPay', icon: '/payment-icons/speedpay.png' },
  { name: 'PayID', icon: '/payment-icons/payid.png' },
  { name: 'Visa', icon: '/payment-icons/visa.png' },
  { name: 'Osko', icon: '/payment-icons/osko.png' },
];

export default function BestAUSCasinos() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [expandedReview, setExpandedReview] = useState<string | null>(null);

  const filters = ['All', 'New', 'Best Bonus', 'Fast Payout', 'Mobile'];

  const filteredCasinos = casinos.filter((casino) => {
    if (activeFilter === 'All') return true;
    const normalizedName = normalizeCasinoName(casino.name);
    const targetNames = filterNameMap[activeFilter];
    return targetNames ? targetNames.includes(normalizedName) : true;
  });

  const toggleReview = (casinoName: string) => {
    setExpandedReview(expandedReview === casinoName ? null : casinoName);
  };

  return (
    <>
      <Navbar />
      <CategoryPageLayout
        title="Best Australian Online Casinos 2026"
        subtitle="Expert-reviewed and AUS player approved"
        description="Discover the top-rated online casinos for Australian players. Our experts have tested and reviewed hundreds of casinos to bring you the safest, most rewarding gaming experiences with fast payouts, generous bonuses, and thousands of games."
        lastUpdated="Feb 15, 2026"
        heroVariant="editorial"
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Best AUS Casinos', href: '/best-aus-casinos' },
        ]}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Filter Tabs */}
            <FilterTabs filters={filters} activeFilter={activeFilter} onFilterChange={setActiveFilter} />

            {/* Results Count */}
            <div className="mb-6 text-gray-600">
              Showing {filteredCasinos.length} casino{filteredCasinos.length !== 1 ? 's' : ''}
            </div>

            {/* Casino Cards */}
            <div className="space-y-6">
              {filteredCasinos.map((casino) => (
                <div key={casino.name} className="space-y-4">
                  <CasinoCard casino={casino} onReviewClick={() => toggleReview(casino.name)} />

                  {/* Expandable Review Panel */}
                  <CasinoReviewPanel
                    casino={casino}
                    isOpen={expandedReview === casino.name}
                    onClose={() => setExpandedReview(null)}
                  />
                </div>
              ))}
            </div>

            {/* Review Methodology */}
            <section className="mt-12 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-8 border border-purple-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                What we look at when reviewing real money casinos
              </h3>
              <p className="text-gray-700 text-base leading-relaxed mb-8">
                We rigorously test each of the real money online casinos we encounter as part of our
                25-step review process. Here&apos;s an insight into the rating system and the key factors
                our experts consider before recommending any sites:
              </p>

              <div className="relative pl-6 border-l-2 border-purple-600 space-y-10">
                {reviewCriteria.map((item) => (
                  <div key={item.title} className="relative">
                    <div className="absolute -left-[45px] top-0 w-9 h-9 rounded-full bg-purple-700 text-white flex items-center justify-center shadow-md">
                      <i className="ri-check-line text-lg"></i>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] items-center gap-6 md:gap-8">
                      <div>
                        <h4 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h4>
                        <p className="text-gray-700 leading-relaxed">{item.description}</p>
                      </div>
                      <img
                        src={item.iconImage}
                        alt={`${item.title} icon`}
                        className="w-20 sm:w-20 md:w-24 lg:w-28 h-auto object-contain flex-shrink-0"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 rounded-xl bg-white border border-purple-200 p-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-3">Final verdict</h4>
                <p className="text-gray-700 leading-relaxed mb-5">
                  When real money casinos score well in each sector we write detailed reviews to help
                  players make smart choices. Our experts test each online casino regularly to keep our
                  ratings up-to-date. Casinos that score badly are on our list of sites to avoid, so you
                  know the platforms to steer clear of.
                </p>
                <div className="inline-flex px-6 py-3 bg-purple-700 text-white font-semibold rounded-lg transition-colors"
                >
                  Our Recommendations
                </div>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-6">
              {/* Our Verdict Box */}
              <div className="bg-white rounded-xl border-2 border-orange-500 p-6 shadow-lg">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-lg">
                    <i className="ri-trophy-line text-xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Our Verdict</h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Australian players have access to some of the world's best online casinos. Our top
                  picks offer generous welcome bonuses, thousands of games from leading providers, and
                  fast withdrawals in AUD.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <i className="ri-star-fill text-orange-500 mt-1"></i>
                    <span className="text-sm text-gray-700">All casinos are licensed and regulated</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-star-fill text-orange-500 mt-1"></i>
                    <span className="text-sm text-gray-700">Fast payouts in Australian dollars</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-star-fill text-orange-500 mt-1"></i>
                    <span className="text-sm text-gray-700">24/7 customer support available</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-star-fill text-orange-500 mt-1"></i>
                    <span className="text-sm text-gray-700">Mobile-optimized gaming experience</span>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl p-5 md:p-6 text-white">
                <h3 className="text-2xl md:text-lg font-bold mb-4 md:mb-5">Quick Statistics</h3>
                <div className="space-y-4 md:space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] items-baseline gap-1 md:gap-3">
                    <div className="text-2xl md:text-3xl font-bold leading-none">{casinos.length}</div>
                    <div className="text-purple-100 md:text-purple-200 text-base md:text-md leading-snug">AUS-friendly casinos</div>
                  </div>
                  <div className="border-t border-purple-400/80 pt-4 md:pt-5">
                    <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] items-baseline gap-1 md:gap-3">
                      <div className="text-2xl md:text-3xl font-bold leading-none">$2,500+</div>
                      <div className="text-purple-100 md:text-purple-200 text-base md:text-md leading-snug">Avg welcome bonus</div>
                    </div>
                  </div>
                  <div className="border-t border-purple-400/80 pt-4 md:pt-5">
                    <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] items-baseline gap-1 md:gap-3">
                      <div className="text-2xl md:text-3xl font-bold leading-none">24-48h</div>
                      <div className="text-purple-100 md:text-purple-200 text-base md:text-md leading-snug">Avg payout time</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Popular Payment Methods */}
              <div className="bg-white rounded-xl border border-gray-200 md:p-6 p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Popular Payment Methods</h3>
                <div className="grid grid-cols-4 gap-3">
                  {popularPaymentMethods.map((method) => (
                    <div
                      key={method.name}
                      className="rounded-lg border border-gray-200 bg-gray-50 md:p-3 p-2 flex items-center justify-center"
                    >
                      <img
                        src={method.icon}
                        alt={method.name}
                        className="w-12 h-12 rounded-full object-cover border border-gray-200"
                      />
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </CategoryPageLayout>
      <Footer />
    </>
  );
}
